// Cognito redirects here with ?code=...&state=... after the user signs in.
// We exchange the code for tokens, then push them to the saved return-to
// path (defaults to /).
//
// Error paths:
//   - missing code/state in the URL → render the error
//   - state doesn't match what we stashed → render the error
//   - code exchange 4xx → render the error
//
// The page is intentionally blank while the exchange is in flight; a few
// hundred milliseconds isn't worth a spinner that flashes and disappears.

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { completeSignIn } from '@/auth/auth-service';

type State = 'pending' | 'done' | { error: string };

export function AuthCallbackPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<State>('pending');

  useEffect(() => {
    const code = params.get('code');
    const stateParam = params.get('state');
    const errorParam = params.get('error');

    if (errorParam) {
      setState({ error: errorParam });
      return;
    }
    if (!code || !stateParam) {
      setState({ error: 'missing_code_or_state' });
      return;
    }

    void (async () => {
      try {
        const { returnTo } = await completeSignIn(code, stateParam);
        setState('done');
        // Replace so the back button doesn't bounce the user to a
        // now-stale callback URL.
        navigate(returnTo || '/', { replace: true });
        // Reload to make sure AuthProvider picks up the new tokens (it
        // listens to storage events but our own tab's writes don't fire
        // those — explicit reload is the simplest correct path).
        window.location.reload();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'sign_in_failed';
        setState({ error: message });
      }
    })();
  }, [navigate, params]);

  if (state === 'pending' || state === 'done') {
    return <main className="mx-auto max-w-3xl px-6 py-16" />;
  }

  return (
    <main className="mx-auto max-w-3xl px-6 py-16">
      <h1 className="text-2xl font-semibold tracking-tight">Sign-in failed</h1>
      <p className="mt-2 text-slate-600">
        Cognito returned: <code className="font-mono text-sm">{state.error}</code>
      </p>
      <p className="mt-6">
        <a className="text-accent hover:text-accent-hover underline underline-offset-2" href="/">
          ← Back to home
        </a>
      </p>
    </main>
  );
}
